/**
 * Modal Orchestrator
 *
 * Centralized system to coordinate multiple modals that need to intercept
 * the add-to-cart button. Modals are executed in priority order, and each
 * can decide whether to show or pass to the next modal.
 *
 * Usage:
 *
 * 1. Register your modal:
 *    modalOrchestrator.register({
 *      name: 'my-modal',
 *      priority: 10,
 *      shouldShow: (context) => { return true/false },
 *      show: (context, onComplete) => {
 *        // Show modal, then call onComplete(approved) when done
 *      }
 *    });
 *
 * 2. Intercept add-to-cart button:
 *    addToCartBtn.addEventListener('click', (e) => {
 *      e.preventDefault();
 *      // If trigger button and submit button are the same:
 *      modalOrchestrator.handleAddToCart(addToCartBtn);
 *
 *      // If you need different buttons for trigger vs. final submission:
 *      modalOrchestrator.handleAddToCart(triggerBtn, {
 *        addToCartButton: actualSubmitBtn
 *      });
 *    });
 */

class ModalOrchestratorClass {
  constructor() {
    console.log("ModalOrchestrator initialized");
    /**
     * @type {Array<{name: string, priority: number, shouldShow: function, show: function}>}
     */
    this.modals = [];
    this.isProcessing = false;
    this.processingTimeoutId = null;
    this.managedButtons = new Map(); // Store button -> {element, handler, bypass} mappings
  }

  /**
   * Register a button for the orchestrator to manage
   * @param {HTMLButtonElement} button - The button that will trigger the modal chain
   * @param {HTMLElement | null} customElement - The custom element that owns this button (optional)
   * @param {Object} [context] - Context to pass to the modal chain (optional)
   * @param {HTMLButtonElement} [context.addToCartButton] - The button to use for final form submission (defaults to trigger button)
   */
  registerButton(button, customElement = null, context = {}) {
    if (!button) {
      console.error("[ModalOrchestrator] Cannot register null button");
      return;
    }

    // Don't register twice
    if (this.managedButtons.has(button)) {
      console.warn("[ModalOrchestrator] Button already registered");
      return;
    }

    console.log("[ModalOrchestrator] Registering button:", button);

    // Create click handler
    const handler = /** @param {Event} e */ (e) => {
      const buttonData = this.managedButtons.get(button);

      console.log(
        "[ModalOrchestrator] Button clicked, bypass:",
        buttonData?.bypass
      );

      // Skip if bypass is active
      if (buttonData?.bypass) {
        console.log(
          "[ModalOrchestrator] Bypass active, allowing form submission"
        );
        return;
      }

      e.preventDefault();

      // Check if button is disabled
      if (button.disabled) {
        console.log("[ModalOrchestrator] Button is disabled, ignoring");
        return;
      }

      // Check if limit notice is active
      const notificationWrapper = button.closest(
        ".notification-button-wrapper"
      );
      if (notificationWrapper) {
        const limitNoticePP =
          notificationWrapper.querySelector("limit-notice-pp");
        if (limitNoticePP?.classList.contains("limit-notice-pp--show")) {
          console.log("[ModalOrchestrator] Limit notice active, ignoring");
          return;
        }
      }

      console.log("[ModalOrchestrator] Processing click through orchestrator");
      // Merge stored context with runtime overrides
      this.handleAddToCart(button, {
        customSkuElement: customElement,
        ...buttonData.context,
      });
    };

    // Attach handler
    button.addEventListener("click", handler);

    // Store button data
    this.managedButtons.set(button, {
      element: customElement,
      handler: handler,
      bypass: false,
      context: context,
    });

    console.log("[ModalOrchestrator] Button registered successfully");
  }

  /**
   * Unregister a button
   * @param {HTMLButtonElement} button
   */
  unregisterButton(button) {
    const buttonData = this.managedButtons.get(button);
    if (!buttonData) {
      console.warn("[ModalOrchestrator] Button not registered");
      return;
    }

    button.removeEventListener("click", buttonData.handler);
    this.managedButtons.delete(button);
    console.log("[ModalOrchestrator] Button unregistered");
  }

  /**
   * Register a modal handler
   * @param {Object} config
   * @param {string} config.name - Unique identifier for this modal
   * @param {number} config.priority - Lower numbers execute first (e.g., 10, 20, 30)
   * @param {function(Object): boolean} config.shouldShow - Function that returns true if modal should show
   * @param {function(Object, function): void} config.show - Function that shows the modal, calls onComplete when done
   */
  register(config) {
    console.log("Registering modal:", config.name);
    const { name, priority, shouldShow, show } = config;

    if (!name || priority === undefined || !shouldShow || !show) {
      console.error("[ModalOrchestrator] Invalid modal config:", config);
      return;
    }

    // Remove existing registration with same name
    this.modals = this.modals.filter((m) => m.name !== name);

    // Add new registration
    this.modals.push({ name, priority, shouldShow, show });

    // Sort by priority (lower first)
    this.modals.sort((a, b) => a.priority - b.priority);

    console.log(
      `[ModalOrchestrator] Registered modal: ${name} (priority: ${priority})`
    );
  }

  /**
   * Unregister a modal
   * @param {string} name
   */
  unregister(name) {
    this.modals = this.modals.filter((m) => m.name !== name);
    console.log(`[ModalOrchestrator] Unregistered modal: ${name}`);
  }

  /**
   * Clear any existing processing timeout
   * @private
   */
  _clearProcessingTimeout() {
    if (this.processingTimeoutId) {
      clearTimeout(this.processingTimeoutId);
      this.processingTimeoutId = null;
    }
  }

  /**
   * Reset the processing flag and clear timeout
   * @private
   */
  _resetProcessing() {
    this._clearProcessingTimeout();
    this.isProcessing = false;
  }

  /**
   * Main entry point - call this when add-to-cart is clicked
   * @param {HTMLButtonElement} modalTriggerButton - The button that was clicked to start the modal chain
   * @param {Object} additionalContext - Any additional context to pass to modals
   * @param {Object} additionalContext.addToCartButton - Optional: specific button to use for final form submission (defaults to modalTriggerButton)
   */
  handleAddToCart(modalTriggerButton, additionalContext = {}) {
    if (this.isProcessing) {
      console.warn("[ModalOrchestrator] Already processing, ignoring click");
      return;
    }

    this.isProcessing = true;

    // Safety timeout: reset processing flag after 30 seconds if still stuck
    this._clearProcessingTimeout();
    this.processingTimeoutId = setTimeout(() => {
      console.error(
        "[ModalOrchestrator] Processing timeout - force resetting isProcessing flag"
      );
      this._resetProcessing();
    }, 30000);

    const context = {
      modalTriggerButton,
      addToCartButton: additionalContext.addToCartButton || modalTriggerButton,
      ...additionalContext,
    };

    this._processNextModal(0, context);
  }

  /**
   * Process modals in sequence
   * @private
   */
  _processNextModal(index, context) {
    // If we've gone through all modals, execute the final add-to-cart
    if (index >= this.modals.length) {
      this._resetProcessing();
      this._executeFinalAddToCart(context);
      return;
    }

    console.log("modal constext: ", context);

    const modal = this.modals[index];

    console.log(`[ModalOrchestrator] Checking modal: ${modal.name}`);

    // Check if this modal should show
    let shouldShow;
    try {
      shouldShow = modal.shouldShow(context);
    } catch (error) {
      console.error(
        `[ModalOrchestrator] Error in shouldShow for ${modal.name}:`,
        error
      );
      this._resetProcessing();
      return;
    }

    if (!shouldShow) {
      console.log(`[ModalOrchestrator] Skipping modal: ${modal.name}`);
      // Skip to next modal
      this._processNextModal(index + 1, context);
      return;
    }

    console.log(`[ModalOrchestrator] Showing modal: ${modal.name}`);

    // Show the modal, wait for completion
    try {
      modal.show(context, (approved) => {
        console.log(
          `[ModalOrchestrator] Modal ${modal.name} completed with approved=${approved}`
        );

        if (approved === false) {
          // User cancelled/rejected, stop the chain
          console.log(
            `[ModalOrchestrator] User cancelled at modal: ${modal.name}`
          );
          this._resetProcessing();
          return;
        }

        // Continue to next modal
        this._processNextModal(index + 1, context);
      });
    } catch (error) {
      console.error(
        `[ModalOrchestrator] Error showing modal ${modal.name}:`,
        error
      );
      this._resetProcessing();
    }
  }

  /**
   * Execute the final add-to-cart action
   * @private
   */
  _executeFinalAddToCart(context) {
    console.log(
      "[ModalOrchestrator] All modals completed, executing add-to-cart"
    );

    const { addToCartButton } = context;
    console.log("add to cart button: ", addToCartButton);

    if (!addToCartButton) {
      console.error("[ModalOrchestrator] No add-to-cart button in context");
      return;
    }

    // Set bypass flag on the managed button
    const buttonData = this.managedButtons.get(addToCartButton);
    if (buttonData) {
      buttonData.bypass = true;
      console.log("[ModalOrchestrator] Set bypass flag on managed button");
    } else {
      console.warn("[ModalOrchestrator] Button not managed by orchestrator");
    }

    // Temporarily restore submit behavior
    const originalType = addToCartButton.type;
    addToCartButton.type = "submit";

    // Click the button to trigger form submission
    // Use a small timeout to ensure we're not in the same event loop
    setTimeout(() => {
      addToCartButton.click();
      addToCartButton.type = originalType;

      // Clear bypass flag after submission
      if (buttonData) {
        setTimeout(() => {
          buttonData.bypass = false;
          console.log("[ModalOrchestrator] Cleared bypass flag");
        }, 100);
      }
    }, 50);
  }

  /**
   * Get all registered modals (for debugging)
   */
  getRegisteredModals() {
    return this.modals.map((m) => ({ name: m.name, priority: m.priority }));
  }
}

// Create singleton instance
const modalOrchestrator = new ModalOrchestratorClass();

// Make it globally available
// @ts-ignore - Type is defined in custom_types.js
window.modalOrchestrator = modalOrchestrator;
